import { useCallback, useState } from "react";
import { UnitAvailability } from "@ses-mams/api-contract";
import { Text } from "~/components/ui/text";
import { Stack } from "~/components/ui/stack";
import { AvailabilityReportCell } from "~/components/common/availabilityReportCell";
import { AdvancedMarker } from "~/components/ui/map";
import { ThemeProvider } from "~/components/ui/theme";

type UnitAvailabilityMarkerProps = {
  availability?: UnitAvailability;
  position: google.maps.LatLng;
  children: string;
};

export const UnitAvailabilityMarker = ({
  availability,
  position,
  children,
}: UnitAvailabilityMarkerProps) => {
  const immediatelyAvailable = availability?.immediatelyAvailable ?? 0;
  const committed = availability?.committed ?? 0;
  const available = availability?.available ?? 0;
  const conditional = availability?.conditional ?? 0;
  const unset = availability?.unset ?? 0;
  const unavailable = availability?.unavailable ?? 0;

  const [isHovered, setIsHovered] = useState(false);

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <AdvancedMarker position={position} zIndex={isHovered ? 1000 : 0}>
      <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <ThemeProvider>
          <Stack
            direction="column"
            gap="xsmall"
            background="surface"
            borderRadius="medium"
            paddingX="small"
            paddingY="small"
            sx={{
              maxWidth: "200px",
              width: "100%",
              boxShadow:
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
            }}
          >
            <Stack
              direction="row"
              justify="space-between"
              align="center"
              gap="xsmall"
            >
              <Text size="small" weight="semi">
                {children}
              </Text>
              <Text
                size="small"
                noWrap
                tone="info"
                sx={{
                  flexShrink: 0,
                }}
              >{`${committed} Activated`}</Text>
            </Stack>

            <Stack direction="row" align="center" gap="small">
              <AvailabilityReportCell
                value={immediatelyAvailable}
                status="ImmediatelyAvailable"
              />
              <AvailabilityReportCell value={available} status="Available" />
              <AvailabilityReportCell
                value={conditional}
                status="Conditional"
              />
              <AvailabilityReportCell value={unset} status="Unset" />
              <AvailabilityReportCell
                value={unavailable}
                status="Unavailable"
              />
            </Stack>
          </Stack>
        </ThemeProvider>
      </div>
    </AdvancedMarker>
  );
};
